import { ReactElement, ChangeEvent, useMemo, useCallback } from 'react';
import { Typography, Stack, Collapse, Tooltip } from '@mui/material';
import {
  ChangeCircle as ChangeStateIcon,
  ManageAccounts as ModifyAssignmentIcon,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileArrowDown as ExportListIcon,
  faClone as DuplicateIcon,
  faHistory as MarkAsRevisitIcon,
  faFileInvoiceDollar as BillingIcon,
  faArrowsRotate as RefreshIcon,
} from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import { useAppSelector, useLazyRequest, useToggle } from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { isAdminUser, isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { ItemFilterV2Base } from '~services/item/types';
import {
  searchItemsToExportV2,
  markAsRevisitItems,
  duplicateItems,
} from '~services/item';
import {
  ActionOrderMenu,
  DialogChangeOrderState,
  DialogModifyAssignmentOrders,
  DialogConfimation,
  DialogRebillOrders,
} from '~components/index';
import { ActionOrderMenuOptionItem } from '~components/ActionOrderMenu/types';
import { Nullable } from '~globals/types/commons';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { getFeatureAvailableToDate } from '~utils/feature';
import { formatterDate } from '~utils/formatter';
import { downloadFileByRequest } from '~utils/download';
import { pluralize } from '~utils/commons';

import { useDashboardVariantV2CarrierContext } from '../../../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierFinalizedFiltersContext } from '../../../../../FinalizedFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../../../PresetsContext';
import { useDashboardVariantV2CarrierContentFinalizedTabContext } from '../../Context';

import {
  DashboardVariantV2CarrierContentActionsContainer,
  DashboardVariantV2CarrierContentFormControlWithBackground,
  DashboardVariantV2CarrierContentFormControlCheckBox,
  DashboardVariantV2CarrierContentActionsIconButton,
  DashboardVariantV2CarrierContentActionsIconButtonIcon,
} from '../../../../styles';

const DashboardVariantV2CarrierContentFinalizedTabActions =
  (): ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAppSelector(selectAuth);

    const isAdmin = useMemo(() => isAdminUser(user), [user]);
    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
    const isUserOnlyCreatorOrder = useMemo(
      () => isOnlyCreatorOrder(user),
      [user],
    );

    const {
      data: { reloadDataByConfig: reloadMainDataByConfig },
    } = useDashboardVariantV2CarrierContext();

    const {
      filters: { handleClearFilters, handleClearFilterByValues },
      forceRequest: { toggleValue: toggleForceRequest },
    } = useDashboardVariantV2CarrierFinalizedFiltersContext();

    const {
      selectedPreset: { reset: handleResetSelectedPreset },
    } = useDashboardVariantV2CarrierPresetsContext();

    const {
      selection: { selectedIds, utils: selectedIdsUtils },
      parseFiltersRequest,
      table: {
        queryParams: { pagination },
      },
      actions: { reloadDataAndResetSelectedRows, handleShowSuccessAlert },
      isOnlyTodayDelivery: {
        value: isOnlyTodayDelivery,
        toggleValue: toggleIsOnlyTodayDelivery,
      },
    } = useDashboardVariantV2CarrierContentFinalizedTabContext();

    const isEmptySelectedIds = useMemo(
      () => selectedIdsUtils.isEmpty(),
      [selectedIdsUtils],
    );

    const handleChangeIsTodayDelivery = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target ?? event.currentTarget;
        toggleIsOnlyTodayDelivery(checked);

        let expirationDateTimeFrom: Nullable<Date> = null;
        let expirationDateTimeTo: Nullable<Date> = null;

        if (checked) {
          const currentDate = moment();
          expirationDateTimeFrom = currentDate.startOf('day').toDate();
          expirationDateTimeTo = currentDate.clone().endOf('day').toDate();
        }

        handleClearFilters(false);
        handleClearFilterByValues(
          { expirationDateTimeFrom, expirationDateTimeTo },
          () => toggleForceRequest(true),
        );
        handleResetSelectedPreset();
      },
      [
        handleClearFilterByValues,
        handleClearFilters,
        handleResetSelectedPreset,
        toggleForceRequest,
        toggleIsOnlyTodayDelivery,
      ],
    );

    const [, loadingSearchToExport, , executeSearchToExport] = useLazyRequest({
      request: searchItemsToExportV2,
      withPostSuccess: (response) => {
        if (response.headers && response.data) {
          const bodyRequest = JSON.parse(
            response.config?.data as string,
          ) as ItemFilterV2Base;

          let currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });

          if (bodyRequest.dateFrom && bodyRequest.dateTo) {
            const dateFromDownload = formatterDate(bodyRequest.dateFrom, {
              format: 'YYYYMMDD',
            });

            const dateToDownload = formatterDate(bodyRequest.dateTo, {
              format: 'YYYYMMDD',
            });

            currentDate = `${dateFromDownload}-${dateToDownload}`;
          }

          const defaultFileName = `pedidos-finalizado-${currentDate}.csv`;

          downloadFileByRequest(
            response.headers,
            response.data,
            defaultFileName,
          );

          enqueueSnackbar(`Se ha exportado los pedidos correctamente`, {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(
            'No se pudo exportar los pedidos, intente nuevamente',
            { variant: 'error' },
          );
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo exportar los pedidos, intente nuevamente', {
          variant: 'error',
        });
      },
    });

    const handleExportItems = useCallback(async () => {
      await executeSearchToExport({
        filters: parseFiltersRequest(),
        pagination,
      });
    }, [executeSearchToExport, pagination, parseFiltersRequest]);

    const [openDialogChangeStateOrders, toggleOpenDialogChangeStateOrders] =
      useToggle(false);

    const [
      openDialogModifyAssignmentOrders,
      toggleDialogModifyAssignmentOrders,
    ] = useToggle(false);

    const [openConfirmDuplicateOrders, toggleOpenConfirmDuplicateOrders] =
      useToggle(false);

    const [
      openConfirmDialogMarkAsRevisitOrders,
      toggleOpenConfirmDialogMarkAsRevisitOrders,
    ] = useToggle(false);

    const [openDialogRebillOrders, toggleOpenDialogRebillOrders] =
      useToggle(false);

    const actionsWitSelectionMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
      () => [
        {
          label: 'Cambiar estado de pedidos',
          Icon: <ChangeStateIcon color="success" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleOpenDialogChangeStateOrders(true),
        },
        {
          label: 'Modificar asignación de pedidos',
          Icon: <ModifyAssignmentIcon color="success" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleDialogModifyAssignmentOrders(true),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        {
          label: 'Duplicar destinos',
          Icon: <FontAwesomeIcon icon={DuplicateIcon} />,
          enabled: !isUserOnlyCreatorOrder,
          onClick: () => toggleOpenConfirmDuplicateOrders(true),
        },
        {
          label: 'Marcar como revisita',
          Icon: <FontAwesomeIcon icon={MarkAsRevisitIcon} />,
          enabled: !isUserOnlyReader && !isUserOnlyCreatorOrder,
          onClick: () => toggleOpenConfirmDialogMarkAsRevisitOrders(true),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        {
          label: 'Retarifar pedidos',
          Icon: <FontAwesomeIcon icon={BillingIcon} />,
          enabled: isAdmin,
          onClick: () => toggleOpenDialogRebillOrders(true),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmBillingGiver,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmBillingGiver,
            ),
          },
        },
      ],
      [
        isUserOnlyReader,
        isAdmin,
        isUserOnlyCreatorOrder,
        toggleOpenDialogChangeStateOrders,
        toggleDialogModifyAssignmentOrders,
        toggleOpenConfirmDuplicateOrders,
        toggleOpenConfirmDialogMarkAsRevisitOrders,
        toggleOpenDialogRebillOrders,
      ],
    );

    const actionsGeneralMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
      () => [
        {
          label: 'Exportar listado',
          Icon: <FontAwesomeIcon icon={ExportListIcon} />,
          loading: loadingSearchToExport,
          enabled: true,
          disabled: false,
          onClick: handleExportItems,
        },
      ],
      [loadingSearchToExport, handleExportItems],
    );

    const handleSuccessChangeOrdersState = useCallback(() => {
      toggleOpenDialogChangeStateOrders(false);

      reloadDataAndResetSelectedRows();
    }, [reloadDataAndResetSelectedRows, toggleOpenDialogChangeStateOrders]);

    const [, loadingMarkAsRevisitOrders, , executeMarkAsRevisitOrders] =
      useLazyRequest({
        request: markAsRevisitItems,
        withPostSuccess: (response) => {
          toggleOpenConfirmDialogMarkAsRevisitOrders();

          const revisitedCount = response?.data?.data?.length ?? 0;
          const requestedCount = selectedIds.length;

          if (revisitedCount === 0) {
            enqueueSnackbar(
              // eslint-disable-next-line max-len
              `Los pedidos no tienen visita fallida y no se marcaron como revisita.`,
              { variant: 'warning' },
            );

            return;
          }

          let successTitle = 'Pedidos marcados como revisita correctamente';

          if (revisitedCount < requestedCount) {
            // eslint-disable-next-line max-len
            const firstSentence = `${revisitedCount} de ${requestedCount} pedidos se marcaron para revisita`;

            const secondSentence = pluralize({
              singular: 'pedido no tiene una visita fallida.',
              plural: 'pedidos tienen una visita fallida.',
              count: requestedCount - revisitedCount,
              includeCount: true,
            });

            successTitle = `${firstSentence} ${secondSentence}`;
          }

          handleShowSuccessAlert(
            successTitle,
            'Los pedidos ahora están disponibles en la sección de pedidos EN DEPOSITO.',
          );

          reloadDataAndResetSelectedRows();
        },
        withPostFailure: () => {
          enqueueSnackbar(
            'No se pudo marcar los pedidos como revisita, intente nuevamente',
            { variant: 'error' },
          );
        },
      });

    const handleMarkAsRevisitOrders = useCallback(() => {
      if (!isEmptySelectedIds) {
        executeMarkAsRevisitOrders(selectedIds);
      }
    }, [executeMarkAsRevisitOrders, isEmptySelectedIds, selectedIds]);

    const [, loadingDuplicateOrders, , executeDuplicateOrders] = useLazyRequest(
      {
        request: duplicateItems,
        withPostSuccess: () => {
          enqueueSnackbar('Destinos repetidos correctamente', {
            variant: 'success',
          });

          toggleOpenConfirmDuplicateOrders();

          reloadDataAndResetSelectedRows();
        },
        withPostFailure: () => {
          enqueueSnackbar(
            'No se pudo repetir los destinos, intente nuevamente',
            { variant: 'error' },
          );
        },
      },
    );

    const handleDuplicateOrders = useCallback(() => {
      if (!isEmptySelectedIds) {
        executeDuplicateOrders(selectedIds);
      }
    }, [executeDuplicateOrders, isEmptySelectedIds, selectedIds]);

    const handleRefreshData = useCallback(() => {
      reloadDataAndResetSelectedRows();
      reloadMainDataByConfig(false);
    }, [reloadMainDataByConfig, reloadDataAndResetSelectedRows]);

    return (
      <DashboardVariantV2CarrierContentActionsContainer justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography component="h6" variant="h6" fontWeight="bold">
            PEDIDOS FINALIZADOS
          </Typography>

          <Tooltip title="Refrescar listado" arrow>
            <DashboardVariantV2CarrierContentActionsIconButton
              color="secondary"
              variant="outlined"
              onClick={handleRefreshData}
            >
              <DashboardVariantV2CarrierContentActionsIconButtonIcon
                icon={RefreshIcon}
              />
            </DashboardVariantV2CarrierContentActionsIconButton>
          </Tooltip>

          {!isUserOnlyReader && (
            <Collapse
              in={!isEmptySelectedIds}
              timeout="auto"
              unmountOnExit
              orientation="horizontal"
            >
              <ActionOrderMenu
                options={actionsWitSelectionMenuOptions}
                buttonProps={{ label: 'Selección', sx: { minHeight: 40 } }}
              />
            </Collapse>
          )}
        </Stack>

        <Stack direction="row" spacing={2}>
          <DashboardVariantV2CarrierContentFormControlWithBackground
            control={
              <DashboardVariantV2CarrierContentFormControlCheckBox
                checked={isOnlyTodayDelivery}
                onChange={handleChangeIsTodayDelivery}
              />
            }
            label="Solo envíos que vencen hoy"
          />

          {!isUserOnlyReader && (
            <ActionOrderMenu options={actionsGeneralMenuOptions} />
          )}
        </Stack>

        <DialogChangeOrderState
          open={openDialogChangeStateOrders}
          onClose={() => toggleOpenDialogChangeStateOrders(false)}
          itemsIds={selectedIds}
          onChangeSuccess={handleSuccessChangeOrdersState}
        />

        {openDialogModifyAssignmentOrders && (
          <DialogModifyAssignmentOrders
            open={openDialogModifyAssignmentOrders}
            onClose={() => toggleDialogModifyAssignmentOrders(false)}
            itemIds={selectedIds}
            onSuccessModify={() => reloadDataAndResetSelectedRows()}
          />
        )}

        <DialogConfimation
          open={openConfirmDuplicateOrders}
          description="Los destinos seleccionados serán repetidos"
          onConfirm={handleDuplicateOrders}
          onCancel={() => toggleOpenConfirmDuplicateOrders(false)}
          loadingConfirm={loadingDuplicateOrders}
        />

        <DialogConfimation
          open={openConfirmDialogMarkAsRevisitOrders}
          description="Los pedidos seleccionados serán marcados como revisitas"
          onConfirm={handleMarkAsRevisitOrders}
          onCancel={() => toggleOpenConfirmDialogMarkAsRevisitOrders()}
          loadingConfirm={loadingMarkAsRevisitOrders}
        />

        <DialogRebillOrders
          open={openDialogRebillOrders}
          onClose={() => toggleOpenDialogRebillOrders(false)}
          itemIds={selectedIds}
          onSuccessSubmit={() => reloadDataAndResetSelectedRows()}
          excludeMode="manual"
        />
      </DashboardVariantV2CarrierContentActionsContainer>
    );
  };

export default DashboardVariantV2CarrierContentFinalizedTabActions;
